<template>
  <div id="webviewer" ref="viewer" />
</template>

<script>
import WebViewer from '@pdftron/webviewer'

import PDFTronAnnotationsManager from './pdftron-annotations-manager'
import PDFTronAnnotationsListener from './pdftron-annotations-listener'
import {
  ANNOTATION_RECTANGLE_TOOL_BUTTON,
  ANNOTATION_SELECT_TOOL_BUTTON,
} from '../constants/pdf-annotations'
import { SfLog } from '@app/adapters/logger'
import PdftronAiProposalMediator from '@app/application/reconciliation_proposal/mediators/mediator'

export default {
  name: 'PDFTron',
  props: {
    path: { type: String },
    initialDocs: { type: Array },
    ownerId: { type: [Number, String] },
    ownerType: { type: String },
    enableAnnotations: { type: Boolean, required: false, default: false },
    enableAnnotationsToolbar: {
      type: Boolean,
      required: false,
      default: false,
    },
    fitWidth: { type: Boolean, required: false, default: false },
    disableMouseWheelZoom: { type: Boolean, required: false, default: false },
    enableAiProposalDrawer: { type: Boolean, required: false, default: false },
    hideTabButtons: { type: Boolean, required: false, default: false },
  },

  computed: {
    webviewerConfig() {
      const annotationEditingTools = [
        'annotationPopup',
        'textPopup',
        ANNOTATION_SELECT_TOOL_BUTTON,
        ANNOTATION_RECTANGLE_TOOL_BUTTON,
      ]
      const disabledOptionalElements = []

      // 1. Annotations enabled
      if (this.enableAnnotations) {
        // a. disable the header initially, so it doesn't "flash" in its default state (we're modifying some buttons there).it is re-enabled after the PDF has loaded
        disabledOptionalElements.push('header')

        // b. annotations toolbar disabled (e.g. Pulse user): disable all annotation editing tools
        if (!this.enableAnnotationsToolbar) {
          disabledOptionalElements.push(...annotationEditingTools)
        }
      } else {
        // 2. Annotations disabled: disable all annotation editing tools
        disabledOptionalElements.push(...annotationEditingTools)
      }

      return {
        licenseKey:
          'Silverfin NV:OEM:Silverfin::B+:AMS(20250427):CA5759031F3794D0533352184F516F2F2292CC238D7C39869D0C6EF05AFA31F5C7',
        path: this.path,
        initialDoc: this.initialDocs,
        disabledElements: [
          'panToolButton',
          'leftPanelButton',
          //'viewControlsButton',
          // hiding view toolbar option since only option left
          'toolbarGroup-View',
          // all toolbar options with editing options disabled
          'toolbarGroup-Shapes',
          'toolbarGroup-Insert',
          'toolbarGroup-Edit',
          'toolbarGroup-FillAndSign',
          'toolbarGroup-Forms',
          'toolbarGroup-Annotate',
          // comments
          'toggleNotesButton',
          // dark theme chooser
          'themeChangeButton',
          // thumbnail toolbar
          'thumbnailControl',
          // page setting editing options
          'rotateHeader',
          //'rotateClockwiseButton',
          //'rotateCounterClockwiseButton',
          'toggleCompareModeButton',
          'viewControlsDivider2',
          // text selection popups
          'contextMenuPopup',
          // entire settings menu + search - disabled for now
          'menuButton',
          'searchButton',
          // opens an edit PDF menu with options that interact weirdly with annotations
          'thumbMultiSelect',
          'toolsHeader',
        ].concat(disabledOptionalElements),
        documentXFDFRetriever: async () => {
          if (this.pdfAnnotationsListener) {
            const annotations =
              await this.pdfAnnotationsListener.fetchAnnotations()
            return annotations.map((i) => i.xfdfString)
          } else {
            return []
          }
        },
      }
    },
  },
  async mounted() {
    try {
      const webviewer = await WebViewer(this.webviewerConfig, this.$el)
      // To prevent SXSS
      webviewer.Core.disableEmbeddedJavaScript()

      if (this.hideTabButtons) {
        const tabsHeader =
          webviewer.UI.iframeWindow.document.getElementsByClassName(
            'TabsHeader'
          )

        if (tabsHeader.length > 0) {
          tabsHeader[0].querySelectorAll('button[aria-label]').forEach((el) => {
            el.style.display = 'none'
          })
        }
      }

      if (this.disableMouseWheelZoom) {
        webviewer.UI.disableFeatures([webviewer.UI.Feature.MouseWheelZoom])
      }

      if (this.enableAiProposalDrawer) {
        this.pdfAiProposalMediator = new PdftronAiProposalMediator(
          webviewer,
          this.initialDocs.length > 1
        )
        this.pdfAiProposalMediator.start()
      }

      if (!this.enableAnnotations) {
        return
      }

      this.pdfAnnotationsManager = new PDFTronAnnotationsManager(
        webviewer,
        this.ownerId,
        this.ownerType,
        this.enableAnnotationsToolbar
      )

      this.pdfAnnotationsListener = new PDFTronAnnotationsListener(
        webviewer,
        this.pdfAnnotationsManager
      )
      this.pdfAnnotationsListener.setup()
    } catch (error) {
      SfLog.error('PDFTron error', {
        error,
      })
    }
  },
  beforeUnmount() {
    if (this.pdfAnnotationsListener) {
      this.pdfAnnotationsListener.cleanup()
    }
    if (this.pdfAiProposalMediator) {
      this.pdfAiProposalMediator.stop()
    }
    this.pdfAnnotationsListener = null
    this.pdfAnnotationsManager = null
    this.pdfAiProposalMediator = null
  },
}
</script>
